import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Stack, Alert } from "@mui/material";

import { useGetRoles } from "../../hooks/roles/useGetRoles";
import { useDeleteRole } from "../../hooks/roles/useDeleteRole";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import { RolesTableColumns } from "./Table/RolesTableColumns";
import RolesTableRow from "./Table/RolesTableRow";

import { useMassDestroyRoles } from "../../hooks/roles/useMassDestroyRoles";
import { useMassRestoreRoles } from "../../hooks/roles/useMassRestoreRoles";
import { useMassForceDeleteRoles } from "../../hooks/roles/useMassForceDeleteRoles";

const BUILT_IN_ROLES = new Set(["admin", "super_admin", "customer"]);

const RolesTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const navigate = useNavigate();

    const [deleteError, setDeleteError] = useState(null);

    const { deleteRoleMutate, isDeleting } = useDeleteRole();
    const { massDestroyRolesMutate, isPending: isMassDeleting } = useMassDestroyRoles();
    const { massRestoreRolesMutate, isPending: isMassRestoring } = useMassRestoreRoles();
    const { massForceDeleteRolesMutate, isPending: isMassForceDeleting } = useMassForceDeleteRoles();

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl, selectedItem,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handlePageChange, handlePerPageChange,
        handleBulkDelete, handleBulkRestore,
    } = useTableActions({
        basePath: "/settings/roles",
        deleteSingle: deleteRoleMutate,
        deleteAll: massDestroyRolesMutate,
        restoreAll: massRestoreRolesMutate,
        forceDeleteAll: massForceDeleteRolesMutate,
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        sort_by: "name",
        sort_order: order,
    }), [page, perPage, search, order]);

    const { roles, meta, isLoading, error, refetch } = useGetRoles(params);

    const onMenuEdit = () => {
        if (!selectedItem) return;
        navigate(`/settings/roles/${selectedItem.id}/edit`);
        handleMenuClose();
    };

    const onMenuDelete = () => {
        if (!selectedItem) return;
        deleteRoleMutate(selectedItem.id, {
            onError: (err) => {
                const payload = err?.response?.data;
                if (payload?.errors?.users_count || payload?.data?.users_count) {
                    setDeleteError({
                        roleName: selectedItem.name,
                        usersCount: payload.errors?.users_count ?? payload.data?.users_count,
                        userIds: payload.errors?.user_ids ?? payload.data?.user_ids,
                    });
                }
            },
        });
        handleMenuClose();
    };

    const renderRow = (rowParams) => (
        <RolesTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "الأدوار والصلاحيات" : "Roles & Permissions"}
                subtitle={isArabic ? "إدارة الأدوار والصلاحيات الممنوحة لكل دور" : "Manage roles and assigned permissions"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "إضافة دور" : "Add Role"}
                onAddClick={() => navigate("/settings/roles/create")}
                isLoading={isLoading}
                error={error}
                items={roles}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={RolesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={onMenuEdit}
                onMenuDelete={onMenuDelete}
                isDeletingSingle={isDeleting}
                menuDisableDelete={selectedItem && BUILT_IN_ROLES.has(selectedItem.name)}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, roles)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={handleBulkRestore}
                isBulkDeleting={isMassDeleting || isMassForceDeleting}
                isBulkRestoring={isMassRestoring}
                emptyLabelAr="لا توجد أدوار"
                emptyLabelEn="No roles"
                refetch={refetch}
            />

            {/* Delete-with-users error dialog */}
            <Dialog
                open={Boolean(deleteError)}
                onClose={() => setDeleteError(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {isArabic ? "لا يمكن حذف الدور" : "Cannot Delete Role"}
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {isArabic
                            ? `الدور "${deleteError?.roleName}" مستخدم من قبل ${deleteError?.usersCount} مستخدم.`
                            : `The role "${deleteError?.roleName}" is assigned to ${deleteError?.usersCount} user(s).`}
                    </Alert>
                    {Array.isArray(deleteError?.userIds) && deleteError.userIds.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" mb={1}>
                                {isArabic ? "أمثلة المستخدمين:" : "Sample users:"}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {deleteError.userIds.slice(0, 10).map((uid) => (
                                    <Button
                                        key={uid}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => navigate(`/users/${uid}`)}
                                    >
                                        #{uid}
                                    </Button>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteError(null)}>
                        {isArabic ? "إغلاق" : "Close"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RolesTable;
